import React, { useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Input,
  Icon,
  StepNavigationButtons,
  GenericModal,
  AnimatedMap
} from '@pinflag/pinflag-ui-kit'
import { DataContext } from '../../context/DataContext'
import axios from 'axios'
import {
  addressParser,
  addressParserUnnormalized
} from '../../helpers/addressParser'
import { InputValidator } from '../../helpers/Validator'
import { onlySomeLetters } from '../../helpers/utils'
import { useForm } from 'react-hook-form'
import { useApiPinflag } from '../../hook/useApiPinflag'
import { ONLY_CHECKOUT } from '../../constants/stores'
import { useVtex } from '../../hook/useVtex'
import { motion, AnimatePresence } from 'framer-motion'

const AddressUserData = ({
  setter,
  selectedPointData,
  mapUrl,
  setSelectedPointData,
  handleAdvanceStep,
  setGiftCardProduct,
  isLoading,
  setIsLoading
}) => {
  const {
    steps,
    setSteps,
    setAddressInfo,
    setCurrentStep,
    addressInfo,
    companyInfo,
    setDeliveryInfo,
    cartData,
    ecommerceInfo,
    setAbandonedCartData
  } = useContext(DataContext)
  const { companyName, secondaryColor } = companyInfo
  const [customErrors, setCustomErrors] = useState({})
  const [citySelected, setCitySelected] = useState('')
  const [stateSelected, setStateSelected] = useState('')
  const [coordsSelected, setCoordsSelected] = useState([])
  const {
    register,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
    setFocus
  } = useForm({
    mode: 'all',
    defaultValues: {
      address: '',
      aditionalInfo: addressInfo.aditionalInfo
    }
  })

  const addressForm = {
    address: { ...register('address', { required: 'Campo requerido' }) },
    aditionalInfo: {
      ...register('aditionalInfo', {
        validate: value =>
          InputValidator.isAditionalInfoValid(value) || 'Máximo 55 caracteres',
        onBlur: () => {
          setCustomErrors({ ...customErrors, aditionalInfo: errors?.aditionalInfo })
        },
        onChange: e => setValue('aditionalInfo', onlySomeLetters(e.target.value, 55))
      })
    }
  }
  const [searchResults, setSearchResults] = useState([])
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDisabled, setIsDisabled] = useState(true)
  const [districtNotAvailable] = useState(false)

  const debounceRef = useRef()

  const { getShippingInfoVtex } = useVtex()

  const onQueryChanged = event => {
    setValue('address', event.target.value)
    setIsPopupOpen(true)

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      searchPlaces(event.target.value)
    }, 400)
  }

  const searchAPI = axios.create({
    baseURL: 'https://api.mapbox.com/geocoding/v5/mapbox.places',
    params: {
      limit: 3,
      language: 'es',
      autocomplete: false,
      country: 'cl',
      access_token: companyInfo.mapboxToken,
      types: 'address'
    }
  })

  const searchPlaces = async addressQuery => {
    if (addressQuery.length > 5) {
      try {
        const resp = await searchAPI.get(`/${addressQuery}.json`)
        const noResults = {
          id: 'No results action ID',
          title: 'No encuentras tu dirección?',
          type: 'action'
        }
        resp.data.features.length > 0
          ? setSearchResults(resp.data.features)
          : setSearchResults([noResults])
      } catch (error) {
        console.log(error)
      }
    }
  }
  const watchAllFields = watch()

  const { calculateShippingPrice } = useApiPinflag()

  useEffect(() => {
    watchAllFields.address.length === 0 && setSearchResults({})
  }, [watchAllFields.address])

  useEffect(() => {
    if (addressInfo.userAddress && !addressInfo.userAddress.includes('*')) {
      setValue('address', addressInfo.userAddress)
      setIsPopupOpen(true)

      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      debounceRef.current = setTimeout(() => {
        searchPlaces(addressInfo.userAddress)
      }, 50)
    }
    const handleResize = () => {
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handleClickAddress = data => {
    const address = addressParser(data.place_name_es)

    setValue('address', address[0])
    setCitySelected(address[1])
    setStateSelected(address[2])
    setCoordsSelected(data.center)
    if (data.address) {
      setSelectedPointData(data)
      setIsPopupOpen(false)
      setIsDisabled(false)
    } else {
      setFocus('address')
    }
  }

  const goManualDirection = () => {
    setAddressInfo(prevState => {
      return {
        ...prevState,
        userAddress: watchAllFields.address,
        district: citySelected,
        coords: coordsSelected,
        region: stateSelected,
        aditionalInfo: watchAllFields.aditionalInfo,
        isManualAddress: true,
        manualDelivery: true
      }
    })
    // go to manual direction
    setter(true)
    setIsModalOpen(false)
    setSelectedPointData(undefined)
  }

  const handleGoConfirmation = () => {
    handleAdvanceStep()
  }

  const handleOpenServiceTypeModal = async (addressData) => {
    if (!isAddressValid(addressData.address)) return setIsModalOpen(true)

    const arrayAddress = [addressData.address, citySelected, stateSelected]
    if (isGiftCardPurchase()) {
      return handleGiftCard(arrayAddress, addressData.aditionalInfo)
    }

    await processShipping(arrayAddress, addressData)
    updateAddressInfo(addressData)
  }

  const isAddressValid = (address) => /\d/.test(address)

  const isGiftCardPurchase = () => {
    return ecommerceInfo.cms === 'shopify' && cartData.lines.every(item => !item.requires_shipping)
  }

  const handleGiftCard = (arrayAddress, additionalInfo) => {
    setGiftCardProduct(arrayAddress, additionalInfo)
  }

  const processShipping = async (arrayAddress, addressData) => {
    setIsLoading(true)
    if (ONLY_CHECKOUT.includes(companyName)) {
      getShippingInfoVtex(
        addressData.address,
        citySelected,
        stateSelected,
        addressData.aditionalInfo,
        setDeliveryInfo,
        setIsLoading,
        handleGoConfirmation
      )
    } else {
      await calculateShippingPrice(arrayAddress, setDeliveryInfo, setIsLoading, handleGoConfirmation, 'address automatic')
    }
  }

  const updateAddressInfo = (addressData) => {
    const arrayAddress = [addressData.address, citySelected, stateSelected]
    setAbandonedCartData(previousState => ({
      ...previousState,
      address: arrayAddress[0],
      city: arrayAddress[1],
      state: arrayAddress[2],
      delivery_instructions: addressData.aditionalInfo
    }))

    setAddressInfo(prevState => ({
      ...prevState,
      userAddress: addressData.address,
      district: citySelected,
      coords: coordsSelected,
      region: stateSelected,
      aditionalInfo: addressData.aditionalInfo,
      isManualAddress: false
    }))
  }

  const handleReturnButton = () => {
    const updatedSteps = [...steps]
    updatedSteps[0].completed = false
    updatedSteps[1].disabled = false
    updatedSteps[1].subSteps[0].completed = false
    updatedSteps[1].subSteps[1].completed = false
    setSteps(updatedSteps)
    setCurrentStep(1)
  }

  return (
    <motion.div className="flex flex-col h-full max-h-[calc(100vh-120px)] overflow-hidden">
      <div className='flex flex-col items-center'>
        <GenericModal
          isOpenModal={isModalOpen}
          icon='error'
          modalTitle='Falta la numeración de la calle'
          modalText='Al parecer no se encuentra tu dirección exacta, puedes ingresarla
              manualmente.'
          firstButton='Volver'
          secondButton='Ingresar Número'
          handleClose={() => setIsModalOpen(false)}
          handleAction={goManualDirection}
        />
      </div>
      <motion.form
        onSubmit={handleSubmit(handleOpenServiceTypeModal)}
        initial={{ height: 'auto' }}
        animate={{ height: 'auto' }}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
      >
        <motion.div
          className="flex flex-col bg-white rounded overflow-y-auto min-h-0 pb-2 md:px-3"
          initial={{ height: 'auto' }}
          animate={{ height: 'auto' }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
        >
          <div className='px-4 pb-4'>
            <Input
              containerClass=''
              label='Calle y Número'
              innerRef={addressForm.address.ref}
              onChange={onQueryChanged}
              onBlur={addressForm.address.onBlur}
              name='address'
              type='text'
              errors={errors?.address}
              secondaryColor={secondaryColor}
              testId="street-number"
            />
            <AnimatePresence>
              {searchResults.length > 0 && isPopupOpen && (
                <motion.ul
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col rounded-[10px] m-0 mr-10 md:w-[287px] md:h-[245px] sm:w-max absolute shadow-xl bg-zinc-50 p-2 z-10 overflow-y-scroll"
                  data-testid="address-list"
                >
                  {searchResults?.map((result, index) => {
                    return (
                      <div key={index}>
                        {result.type && result.type === 'action'
                          ? (
                              ''
                            )
                          : (
                            <div className='flex justify-between md:hover:bg-slate-200 md:hover:rounded-[7px] md:py-[1px]'>
                              <li
                                className='flex flex-col text-sm  cursor-pointer md:text-[10px] md:text-gray-500 md:font-medium leading-[125.02%] md:h-[43px] py-3'
                                onClick={() => handleClickAddress(result)}
                                key={result.id}
                              >
                                <div className='flex flex-col px-4 justify-center h-full'>
                                  <span className='font-semibold md:text-zinc-800'>
                                    {addressParser(result.place_name_es)[0]}
                                  </span>
                                  <span>
                                    {addressParser(result.place_name_es)[1]},
                                    {addressParserUnnormalized(
                                      result.place_name_es
                                    )[2].slice(0, -8)}
                                  </span>
                                </div>
                              </li>
                              <Icon
                                name='diagonal-arrow'
                                width={14}
                                className='fill-gray-300 min-w-[9px] w-[9px] md:mr-3'
                              />
                            </div>
                            )}
                      </div>
                    )
                  })}
                  <hr />
                  <div className='flex h-24 items-center my-2'>
                    <Icon
                      name='search-address'
                      width={37}
                      className='fill-gray-800 ml-3 mr-5'
                    />
                    <div className='text-[10px]'>
                      <li className='text-gray-800'>
                        ¿No encuentras tu dirección?
                      </li>
                      <li className='text-slate-300'>
                        Puedes ingresar la dirección de forma manual
                      </li>
                      <div className='flex'>
                        <Button
                          label='Ingresar manualmente'
                          customStyle={{
                            backgroundColor: secondaryColor
                          }}
                          LeftIcon={
                            <Icon
                              name='edit'
                              width={9}
                              className='fill-white mr-1 text-sm'
                            />
                          }
                          onClick={() => {
                            setter(true)
                          }}
                          className={`flex items-center justify-center bg-cyan-50 px-3 py-1 mt-3 rounded-2xl mr-4 text-[11px] font-light text-white ${ecommerceInfo.cms === 'woocommerce' && 'border'}`}
                          testId='manual-address'
                        />
                      </div>
                    </div>
                  </div>
                </motion.ul>
              )}
            </AnimatePresence>
            <Input
              containerClass='mt-3 z-0'
              label='Detalle'
              innerRef={addressForm.aditionalInfo.ref}
              onChange={addressForm.aditionalInfo.onChange}
              onBlur={addressForm.aditionalInfo.onBlur}
              name='aditionalInfo'
              type='text'
              errors={errors.aditionalInfo}
              customErrors={customErrors.aditionalInfo}
              secondaryColor={secondaryColor}
              testId="additional-info"
            />
            <AnimatePresence>
              {selectedPointData && mapUrl && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                >
                  <AnimatedMap
                    mapUrl={mapUrl}
                    secondaryColor={secondaryColor}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
        <motion.div className="flex-shrink-0 mt-auto bg-slate-100">
          <div className="w-full md:p-4">
            <StepNavigationButtons
              labelOne="Atrás"
              onClickOne={handleReturnButton}
              iconOneName="arrow-back"
              classNameOne="bg-white center text-black"
              labelTwo="Continuar"
              isDisabledTwo={isDisabled || districtNotAvailable}
              roleTwo="submit"
              buttonTwoColor={secondaryColor}
            />
            {districtNotAvailable && (
              <div>
                <span className="pt-2 text-sm text-red-500">
                  No despachamos hacia esa comuna. Elige otra.
                </span>
              </div>
            )}
          </div>
        </motion.div>
      </motion.form>
    </motion.div>
  )
}

AddressUserData.propTypes = {
  setter: PropTypes.func,
  selectedPointData: PropTypes.object,
  mapUrl: PropTypes.string,
  setSelectedPointData: PropTypes.func,
  handleAdvanceStep: PropTypes.func,
  setGiftCardProduct: PropTypes.func,
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func
}

export default AddressUserData
